import { LitElement, css, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { ChatLabels } from "../types.js";
type AfasStatus =
  | "operational"
  | "degraded"
  | "maintenance"
  | "down"
  | "unknown";

const KNOWN_STATUSES: ReadonlySet<AfasStatus> = new Set([
  "operational",
  "degraded",
  "maintenance",
  "down",
  "unknown",
]);

const STATUS_ICONS: Record<AfasStatus, string> = {
  operational: "CheckCircleOutlined",
  degraded: "Warning",
  maintenance: "InfoOutlined",
  down: "ErrorOutlined",
  unknown: "HelpOutlined",
};

// The proxy refreshes its own cache every 60s server-side, so polling faster buys
// nothing. A chat tab stays open for hours, which is why this re-reads at all.
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/** Below this width the appbar has no room for the status in words. */
const COMPACT_MAX_WIDTH = 760;

/**
 * The tooltip only earns its place once the words are gone AND there is a pointer to
 * hover with. afas-tooltip also opens on touchstart, and then waits for a tap elsewhere
 * to close again -- but a long press hands that tap to the browser's own link menu, so
 * the tooltip stays up with no gesture left to dismiss it. On touch the status simply
 * links through to afasstatus.nl, which says more than a tooltip would.
 */
const TOOLTIP_QUERY = `(max-width: ${COMPACT_MAX_WIDTH}px) and (hover: hover)`;

@customElement("help-jonas-appbar-status")
export class HelpJonasAppbarStatus extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      min-width: 0;
      align-items: center;
    }

    .status-tooltip {
      display: flex;
      min-width: 0;
      align-items: center;
    }

    /*
     * afas-button carries the tone vocabulary this status maps onto, but it has no
     * href — it renders a <button> — so a real anchor stays the only way to keep
     * this a navigable, middle-clickable link to afasstatus.nl.
     */
    .status-link {
      /* Block-level, not inline: afas-tooltip slots this into a plain <div>, where an
         inline box would sit on that div's baseline and leave descender space under the
         icon -- 4px of it, which throws the icon-only state off centre. */
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      padding: 2px 8px;
      border-radius: 4px;
      color: #f1f5f9;
      line-height: 1.3;
      text-decoration: none;
      white-space: nowrap;
    }

    .status-link:hover {
      background: rgba(241, 245, 249, 0.12);
    }

    .status-link:focus-visible {
      outline: 2px solid #f1f5f9;
      outline-offset: 2px;
    }

    .status-icon {
      flex: 0 0 auto;
      --icon-size: 18px;
      --icon-color: var(--status-color);
    }

    .status-body {
      display: flex;
      min-width: 0;
      flex-direction: column;
      align-items: flex-start;
      justify-content: center;
      /* Tighter than the link's 1.3: the two lines have to read as one block that sits
         in the middle of the bar, not as a first line with a second one hanging under it. */
      line-height: 1.15;
    }

    /* The eyebrow steps back so the status itself carries the line. A literal rather than
       var(--color-black-300): the appbar stays slate in BOTH themes, while that token flips
       to #383838 in the dark theme and would sink into the bar. */
    .status-label {
      color: #d4d4d4;
    }

    .status-value {
      min-width: 0;
      font-weight: 600;
    }

    /*
     * The AFAS tone ramp keyed to each status, as literals rather than
     * --color-<tone>-400: the appbar keeps its dark slate background in BOTH themes,
     * while those tokens flip to dark values in the dark theme (positive-400 becomes
     * #005500) and would sink into the bar.
     */
    .status-link[data-status="operational"] {
      --status-color: #4ade80;
    }

    .status-link[data-status="degraded"] {
      --status-color: #fbbf24;
    }

    .status-link[data-status="maintenance"] {
      --status-color: #3393df;
    }

    .status-link[data-status="down"] {
      --status-color: #f87171;
    }

    .status-link[data-status="unknown"] {
      --status-color: #94a3b8;
    }

    .status-text {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* Only the icon survives on a narrow appbar. Each status has its own glyph, so it keeps
       saying which one it is; the tooltip and the accessible name carry the wording. */
    @media (max-width: ${COMPACT_MAX_WIDTH}px) {
      .status-body {
        display: none;
      }

      .status-link {
        gap: 0;
        padding: 0 6px;
      }
    }

    @media print {
      :host {
        display: none;
      }
    }

    .visually-hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      margin: -1px;
      padding: 0;
      border: 0;
      clip: rect(0 0 0 0);
      clip-path: inset(50%);
      overflow: hidden;
      white-space: nowrap;
    }
  `;

  @state() private accessor status: AfasStatus = "unknown";

  // Null until the proxy answers; the localized "unknown" label stands in until then.
  @state() private accessor label: string | null = null;

  @property({ attribute: false })
  accessor labels: ChatLabels | null = null;

  @state() private accessor tooltipUseful = false;

  private refreshTimer = 0;

  private pendingRequest: AbortController | null = null;

  private readonly tooltipQuery = globalThis.matchMedia?.(TOOLTIP_QUERY);

  private readonly tooltipListener = (event: MediaQueryListEvent): void => {
    this.tooltipUseful = event.matches;
  };

  private readonly visibilityHandler = (): void => {
    if (document.visibilityState === "visible") void this.loadStatus();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadStatus();
    this.tooltipUseful = this.tooltipQuery?.matches ?? false;
    this.tooltipQuery?.addEventListener("change", this.tooltipListener);
    this.refreshTimer = window.setInterval(() => {
      if (document.visibilityState === "visible") void this.loadStatus();
    }, REFRESH_INTERVAL_MS);
    document.addEventListener("visibilitychange", this.visibilityHandler);
  }

  override disconnectedCallback(): void {
    window.clearInterval(this.refreshTimer);
    this.refreshTimer = 0;
    this.tooltipQuery?.removeEventListener("change", this.tooltipListener);
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    this.pendingRequest?.abort();
    this.pendingRequest = null;
    super.disconnectedCallback();
  }

  private async loadStatus(): Promise<void> {
    this.pendingRequest?.abort();
    const request = new AbortController();
    this.pendingRequest = request;
    try {
      const response = await fetch("/api/afas-status", {
        headers: { Accept: "application/json" },
        signal: request.signal,
      });
      if (!response.ok) return;
      const data = (await response.json()) as {
        status?: string;
        label?: string;
      };
      if (data.status && KNOWN_STATUSES.has(data.status as AfasStatus)) {
        this.status = data.status as AfasStatus;
      }
      if (data.label) this.label = data.label;
    } catch {
      // Keep the last known status; this must never throw to the page.
    } finally {
      if (this.pendingRequest === request) this.pendingRequest = null;
    }
  }

  protected override render() {
    const title = this.getLabel("afasStatusTitle", "AFAS Status");
    const statusLabel = this.statusLabel;
    const description = `${title}: ${statusLabel}`;
    const opensInNewTab = this.getLabel(
      "afasStatusOpensInNewTab",
      "opent in nieuw tabblad",
    );
    return html`<afas-tooltip
        class="status-tooltip"
        content=${description}
        ?disabled=${!this.tooltipUseful}
      >
        <a
          class="status-link"
          data-status=${this.status}
          href="https://www.afasstatus.nl"
          target="_blank"
          rel="noopener noreferrer"
          aria-label=${`${description} (${opensInNewTab})`}
        >
          <afas-icon
            class="status-icon"
            icon=${STATUS_ICONS[this.status]}
            aria-hidden="true"
          ></afas-icon>
          <span class="status-body" aria-hidden="true">
            <span class="status-label status-text">${title}</span>
            <span class="status-value status-text">${statusLabel}</span>
          </span>
        </a>
      </afas-tooltip>
      <span class="visually-hidden" role="status" aria-live="polite"
        >${description}</span
      >`;
  }

  private get statusLabel(): string {
    return this.label ?? this.getLabel("afasStatusUnknown", "Status onbekend");
  }

  private getLabel(key: string, fallback: string): string {
    return this.labels?.[key]?.trim() || fallback;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-appbar-status": HelpJonasAppbarStatus;
  }
}
